/**
 * On selection, move the camera so the selected aircraft is not under a panel.
 *
 * See panelAvoidance.ts for why the camera moves rather than the panels. This
 * hook is the glue: it measures whatever is marked `data-map-panel`, projects
 * the aircraft, and eases the camera by the smallest amount that brings it into
 * the clear. It runs on selection only — never on telemetry — so it can never
 * fight an operator who has deliberately panned away from a drone they left
 * selected.
 */

import { useEffect } from 'react';

import { useMapController } from '@/map/MapCanvas';
import { visiblePointFor, type Rect } from '@/map/panelAvoidance';
import { usePrimarySelectedId, useDroneStore } from '@/stores/droneStore';

/** Marks a floating panel that must not end up on top of the selected track. */
export const MAP_PANEL_SELECTOR = '[data-map-panel]';

const EASE_MS = 450;

export function panelRects(container: DOMRect, doc: Document = document): Rect[] {
  return [...doc.querySelectorAll(MAP_PANEL_SELECTOR)].map((element) => {
    const rect = element.getBoundingClientRect();
    return {
      left: rect.left - container.left,
      top: rect.top - container.top,
      right: rect.right - container.left,
      bottom: rect.bottom - container.top,
    };
  });
}

export function useSelectionInView(): void {
  const controller = useMapController();
  const primaryId = usePrimarySelectedId();

  useEffect(() => {
    if (!primaryId) return undefined;

    // The panel is laid out in the same commit that set the selection, so its
    // rect is only real on the next frame. Measured synchronously, the first
    // selection of a session measures a dock that is not there yet and the
    // camera stays where it was — under the panel that appears a frame later.
    const frame = requestAnimationFrame(() => {
      const map = controller.raw();
      if (!map) return;
      const { lat, lon } = useDroneStore.getState().drones.get(primaryId)?.scalars ?? {};
      if (lat === undefined || lon === undefined) return;
      // 0,0 is the "no fix" position the marker layer also refuses to draw.
      if (lat === 0 && lon === 0) return;

      const canvas = map.getCanvas();
      const viewport = { width: canvas.clientWidth, height: canvas.clientHeight };
      if (viewport.width === 0 || viewport.height === 0) return;

      const here = map.project([lon, lat]);
      const target = visiblePointFor(here, viewport, panelRects(canvas.getBoundingClientRect()));
      if (!target) return;

      // `offset` places the given centre at that many pixels from the middle of
      // the map, which is exactly the question here: put this aircraft there.
      map.easeTo({
        center: [lon, lat],
        offset: [target.x - viewport.width / 2, target.y - viewport.height / 2],
        duration: EASE_MS,
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [controller, primaryId]);
}
